import {
  Breakpoints,
  Button,
  ButtonColor,
  ButtonSize,
  FontWeight,
  formatName,
  IconLink,
  ProfileImage,
  Rating,
  RatingSize,
  RatingTextColor,
  TagSize,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import React, { useMemo } from 'react';

import {
  BadgeType,
  ExperienceType,
  GoalName,
  type Scalars,
} from '../../__generated-gql-types__/globalTypes';
import { LexSlug, useLex } from '../../context/LexContext';
import { getCoachUrl } from '../../utils/coach';
import { formatNumber } from '../../utils/common';
import { APPLICANT_SITE_URL } from '../../utils/constants';
import useCopyToClipboardWithToast from '../../utils/hooks/useCopyToClipboardWithToast';
import { TierBadge } from '../TierBadge';
import Breadcrumbs from '../breadcrumbs/Breadcrumbs';
import { useCategoryBreadcrumbs } from '../breadcrumbs/useCategoryBreadcrumbs';
import { type OrganizationTagDetails } from '../common/OrganizationTag';

import AdditionalExperienceTag from './AdditionalExperienceTag';
import CompanyTag from './CompanyTag';
import { CustomerFavoriteTag } from './CustomerFavoriteTag';
import { SaveCoachButton } from './SaveCoachButton';
import SchoolTag from './SchoolTag';
import { SuperCoachTag } from './SuperCoachTag';
import type { CoachInfoCoachFragment } from './__generated-gql-types__/CoachInfo.generated';

interface CoachInfoProps {
  coach: CoachInfoCoachFragment;
  categoryId: Nullable<Scalars['CategoryUrn']['output']>;
}

const CoachInfo: React.FC<CoachInfoProps> = ({ coach, categoryId }) => {
  const copyToClipboard = useCopyToClipboardWithToast();

  const { isLexEnabled } = useLex();
  const isCoachBadgesEnabled = isLexEnabled(LexSlug.COACH_BADGES);
  const isCustomerFavorite = useMemo(() => {
    return coach.badges?.some(
      (badge) =>
        badge.type === BadgeType.CUSTOMER_FAVORITE &&
        badge.categoryUrn === categoryId,
    );
  }, [coach.badges, categoryId]);

  const isSm = useMediaQuery(Breakpoints.SM);

  const shouldShowCoachTier = false;

  const selectedCategory = useMemo(
    () =>
      coach.categoryInformationList?.find(
        (categoryInfo) => categoryInfo.category.id == categoryId,
      ),
    [coach.categoryInformationList, categoryId],
  );

  const hasEducationAdmissionsCommitteeExperience =
    coach.educationExperiences?.some(
      (education) => education.admissionCommittee,
    );
  const hasProfessionalAdmissionsCommitteeExperience =
    coach.professionalExperiences?.some(
      (experience) =>
        experience.experienceType === ExperienceType.ADMISSIONS_COMMITTEE,
    );
  const hasProfessionalHiringExperience = coach.professionalExperiences?.some(
    (experience) => experience.experienceType === ExperienceType.HIRING_MANAGER,
  );

  const formattedCoachName = formatName(coach.user);

  const breadcrumbs = useCategoryBreadcrumbs({
    category: selectedCategory?.category,
  });

  const PROFILE_ACTION_BUTTONS = (
    <div className="flex items-center gap-2">
      {isLexEnabled(LexSlug.SAVING_COACHES_AB_ENABLED) ? (
        <SaveCoachButton
          coachUrn={coach.id}
          isSaved={!!coach.isSaved}
          categoryUrn={categoryId}
        />
      ) : null}
      <Button
        size={ButtonSize.MEDIUM}
        buttonColor={ButtonColor.WHITE}
        LeftIcon={IconLink}
        id="coach_info--copy_link"
        label=""
        hideLabel
        onClick={() =>
          copyToClipboard(
            `${APPLICANT_SITE_URL}${getCoachUrl({
              coachSlug: coach.slug,
              categorySlug: selectedCategory?.category.slug,
            })}`,
            'profile link',
          )
        }
      />
    </div>
  );

  const tagDetails: Partial<OrganizationTagDetails> = {
    size: TagSize.LARGE,
    fontWeight: FontWeight.NORMAL,
    largeIcons: true,
  };

  return (
    <>
      {breadcrumbs.length > 0 || !isSm ? (
        <div className="mb-4 flex w-full justify-between">
          <Breadcrumbs breadcrumbs={breadcrumbs} />
          {PROFILE_ACTION_BUTTONS}
        </div>
      ) : null}
      <div className="flex w-full flex-col items-center md:flex-row md:items-start">
        <ProfileImage
          className="size-32"
          pictureLink={coach.user.pictureLink}
          firstName={coach.user.firstName}
          lastName={coach.user.lastName}
          squareBorder
        />

        <div className="ml-0 mt-6 flex w-full flex-col items-center text-center md:ml-8 md:mt-0 md:items-start md:text-left">
          <div className="flex w-full items-center justify-center md:justify-between">
            <div className="flex items-center">
              <p className="shrink-0 border-r pr-2 text-lg font-medium leading-4 text-leland-gray-dark">
                {formattedCoachName}
              </p>
              {coach.reviewTotal ? (
                <div className="line-clamp-1 flex items-center px-2 text-lg font-normal text-leland-gray-light">
                  <Rating
                    totalStars={1}
                    rate={coach.averageReviewRate}
                    showRate
                    size={RatingSize.MEDIUM}
                    textColor={RatingTextColor.DARK}
                  />

                  <p className="ml-1 line-clamp-1 text-leland-gray-extra-light">
                    ({coach.reviewTotal})
                  </p>
                </div>
              ) : null}
              {shouldShowCoachTier ? (
                <>
                  {/* only show divider if rating is also shown */}
                  {coach.reviewTotal ? (
                    <div className="size-0.5 rounded bg-leland-gray-light" />
                  ) : null}
                  <TierBadge
                    tier={coach.tier}
                    goal={selectedCategory?.category.goal}
                  />
                </>
              ) : null}
              {coach.totalCoachingMinutes > 1000 ? (
                <p className="border-l pl-2 text-lg leading-4 text-leland-gray-extra-light">
                  {formatNumber(coach.totalCoachingMinutes)} min coaching
                </p>
              ) : null}
              {isCoachBadgesEnabled &&
              isCustomerFavorite &&
              selectedCategory?.category.name &&
              isSm ? (
                <div className="ml-2">
                  <CustomerFavoriteTag
                    categoryName={selectedCategory?.category.name}
                  />
                </div>
              ) : null}
            </div>
            <div>
              {selectedCategory || !isSm ? null : PROFILE_ACTION_BUTTONS}
            </div>
          </div>
          {selectedCategory?.headline ? (
            <h2 className="mt-1 text-2xl font-medium text-leland-gray-dark">
              {selectedCategory.headline}
            </h2>
          ) : null}
          {!isSm &&
          isCoachBadgesEnabled &&
          isCustomerFavorite &&
          selectedCategory?.category.name ? (
            <div className="mt-2">
              <CustomerFavoriteTag
                categoryName={selectedCategory?.category.name}
              />
            </div>
          ) : null}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2 md:justify-start">
            {coach.isSuperCoach && isCoachBadgesEnabled ? (
              <SuperCoachTag isLarge />
            ) : null}
            <SchoolTag coach={coach} selectedSchoolTagDetails={tagDetails} />

            <CompanyTag coach={coach} selectedCompanyTagDetails={tagDetails} />

            {selectedCategory?.category.goal === GoalName.SCHOOL &&
            (hasEducationAdmissionsCommitteeExperience ||
              hasProfessionalAdmissionsCommitteeExperience) ? (
              <AdditionalExperienceTag
                type={ExperienceType.ADMISSIONS_COMMITTEE}
                fontWeight={FontWeight.NORMAL}
                size={TagSize.LARGE}
              />
            ) : null}
            {selectedCategory?.category.goal === GoalName.CAREER &&
            hasProfessionalHiringExperience ? (
              <AdditionalExperienceTag
                type={ExperienceType.HIRING_MANAGER}
                fontWeight={FontWeight.NORMAL}
                size={TagSize.LARGE}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

export default CoachInfo;
